type MenuScrollSpyReadingLineInput = {
  viewportHeight: number;
  rootScrollPaddingTop: number;
  sectionScrollMarginTop: number;
};

type CenteredHorizontalScrollInput = {
  currentScrollLeft: number;
  itemLeft: number;
  itemWidth: number;
  containerLeft: number;
  containerWidth: number;
};

type NearestVerticalScrollInput = {
  currentScrollTop: number;
  itemTop: number;
  itemHeight: number;
  containerTop: number;
  containerHeight: number;
  padding?: number;
};

function finiteOrZero(value: number) {
  return Number.isFinite(value) ? value : 0;
}

/**
 * Keep the scrollspy's activation line aligned with where an in-page menu
 * anchor settles. Browsers add the root scroll padding and the section scroll
 * margin together, so omitting either value can make a tapped category revert
 * to the previous chip after smooth scrolling completes.
 */
/**
 * Activate the last category whose section top has crossed the reading line.
 * Containment checks leave the previous chip selected in the gap between
 * sections (and while the next heading is already on screen under sticky chrome).
 */
export function getActiveCategoryFromSectionTops(
  sections: ReadonlyArray<{ key: string; top: number }>,
  readingLine: number,
  options?: { pinnedToEnd?: boolean },
): string | undefined {
  if (options?.pinnedToEnd && sections.length > 0) {
    return sections[sections.length - 1]?.key;
  }
  let active: string | undefined;
  for (const section of sections) {
    if (section.top <= readingLine) {
      active = section.key;
    }
  }
  return active ?? sections[0]?.key;
}

export function getMenuScrollSpyReadingLine({
  viewportHeight,
  rootScrollPaddingTop,
  sectionScrollMarginTop,
}: MenuScrollSpyReadingLineInput) {
  const viewportReadingLine = finiteOrZero(viewportHeight) * 0.28;
  const anchorReadingLine =
    finiteOrZero(rootScrollPaddingTop) +
    finiteOrZero(sectionScrollMarginTop) +
    2;

  return Math.max(176, viewportReadingLine, anchorReadingLine);
}

/**
 * Center an active category chip inside its own horizontal scroller without
 * asking scrollIntoView to move the page vertically.
 */
export function getCenteredHorizontalScrollLeft({
  currentScrollLeft,
  itemLeft,
  itemWidth,
  containerLeft,
  containerWidth,
}: CenteredHorizontalScrollInput) {
  const centeredLeft =
    finiteOrZero(currentScrollLeft) +
    finiteOrZero(itemLeft) -
    finiteOrZero(containerLeft) -
    (finiteOrZero(containerWidth) - finiteOrZero(itemWidth)) / 2;

  return Math.max(0, centeredLeft);
}

/**
 * Reveal an active item inside its own vertical scroller without allowing the
 * browser to move any ancestor scrollers (including the document).
 */
export function getNearestVerticalScrollTop({
  currentScrollTop,
  itemTop,
  itemHeight,
  containerTop,
  containerHeight,
  padding = 0,
}: NearestVerticalScrollInput) {
  const safeScrollTop = finiteOrZero(currentScrollTop);
  const safePadding = Math.max(0, finiteOrZero(padding));
  const itemStart = finiteOrZero(itemTop);
  const itemEnd = itemStart + finiteOrZero(itemHeight);
  const visibleStart = finiteOrZero(containerTop) + safePadding;
  const visibleEnd =
    finiteOrZero(containerTop) +
    finiteOrZero(containerHeight) -
    safePadding;

  if (itemStart < visibleStart) {
    return Math.max(0, safeScrollTop - (visibleStart - itemStart));
  }

  if (itemEnd > visibleEnd) {
    return Math.max(0, safeScrollTop + (itemEnd - visibleEnd));
  }

  return Math.max(0, safeScrollTop);
}
